import { useState, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Sheet, SheetContent, SheetDescription, SheetTitle } from '@/components/ui/sheet';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { ProjectFormModal } from '@/features/operations-projects/components/ProjectFormModal';
import {
  QuoteFormModal,
  useQuoteByNumber,
  useQuoteV3,
  useQuoteV4,
  useUpdateQuote,
  useDeleteQuote,
  useDiscardQuote,
} from '@/features/quotes';
import { useHasQuotePermission } from '@/features/quotes/hooks/useQuotePermission';
import { QUOTE_PERMISSIONS } from '@/features/quotes/permissions';
import { quoteKeys } from '@/features/quotes/hooks/queryKeys';
import {
  useProject,
  useCreateProject,
  useUpdateProject,
  useCompleteMilestone,
  useUncompleteMilestone,
} from '@/features/operations-projects/hooks/useProjects';
import { ProjectsKanbanBoard } from './ProjectsKanbanBoard';
import { OperationsKanbanSummaryBar } from './OperationsKanbanSummaryBar';
import { KanbanToolbar } from './KanbanToolbar';
import { QuoteOperationsDetailPanel } from './QuoteOperationsDetailPanel';
import { ShippingMacValidationDialog } from './ShippingMacValidationDialog';
import { BackwardMoveReasonDialog } from './BackwardMoveReasonDialog';
import { useProjectsKanban, useAdvanceProjectStage, useUpdateProjectPositions, kanbanKeys } from '../hooks/useOperationsMetrics';
import type { CreateProjectInput, InitialProjectQuote, UpdateProjectInput } from '@/features/operations-projects/types';
import type { CreateQuoteInput, UpdateQuoteInput } from '@/features/quotes';
import { UNIVERSAL_STAGES } from '../types';
import { computeStageBucketSummary } from '../quoteSummary';
import { computeWeightedPipeline } from '../weightedPipeline';
import type { KanbanQueryParams, ProjectKanbanCard, StageKey } from '../types';

const STAGE_INDEX: Record<string, number> = Object.fromEntries(
  UNIVERSAL_STAGES.map((s, i) => [s.key, i]),
);
const STAGE_LABEL: Record<string, string> = Object.fromEntries(
  UNIVERSAL_STAGES.map((s) => [s.key, s.label]),
);

export function toInitialSupportProjectQuote(quoteForSupportProject: ProjectKanbanCard | null): InitialProjectQuote | null {
  if (!quoteForSupportProject) return null;
  return {
    id: quoteForSupportProject.quoteId || quoteForSupportProject.id,
    quote_number: quoteForSupportProject.quoteNumber || quoteForSupportProject.projectNumber,
    account_id: quoteForSupportProject.accountId || undefined,
    account_name: quoteForSupportProject.accountName || '',
    project_name: quoteForSupportProject.projectName || quoteForSupportProject.quoteTitle || '',
    quote_title: quoteForSupportProject.quoteTitle || quoteForSupportProject.projectName || '',
    total: quoteForSupportProject.amount,
    currency: quoteForSupportProject.currency || 'CAD',
    commercial_status: quoteForSupportProject.commercialStatus || null,
  };
}

export function ProjectsKanbanView() {
  const queryClient = useQueryClient();
  const { t } = useTranslation('operations');
  const canDiscardQuote = useHasQuotePermission(QUOTE_PERMISSIONS.edit);
  const canDeleteQuote = useHasQuotePermission(QUOTE_PERMISSIONS.delete);
  // The creation-date window defaults to the current year; Clear widens to all time.
  const currentYear = new Date().getFullYear();
  const [filters, setFilters] = useState<KanbanQueryParams>({
    filter: 'all',
    fromDate: `${currentYear}-01-01`,
    toDate: `${currentYear}-12-31`,
  });
  const [selectedQuote, setSelectedQuote] = useState<ProjectKanbanCard | null>(null);
  const [quoteForSupportProject, setQuoteForSupportProject] = useState<ProjectKanbanCard | null>(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isInvoiceWorkflowOpen, setIsInvoiceWorkflowOpen] = useState(false);
  const [invoiceQuoteNumber, setInvoiceQuoteNumber] = useState<string | null>(null);
  const [isDiscardDialogOpen, setIsDiscardDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  // Pin the card at dialog-open time so async mutations read a stable reference
  const [discardingCard, setDiscardingCard] = useState<ProjectKanbanCard | null>(null);
  const [deletingCard, setDeletingCard] = useState<ProjectKanbanCard | null>(null);
  // Moving a card to "ready_to_ship" is intercepted: the MAC-validation window must confirm
  // the flashed device set before the card actually advances (C2b). A separate Shipping
  // prompt is planned later; moving straight to "shipping" advances without a gate.
  const [pendingReadyToShipMove, setPendingReadyToShipMove] = useState<{
    quoteId: string;
    quoteNumber: string | null;
    targetStage: ProjectKanbanCard['currentStage'];
  } | null>(null);
  // Dragging a card BACKWARD to an earlier stage is intercepted: the coordinator
  // must give a written reason, persisted (with their identity) to the
  // append-only operations audit log, before the card actually moves back.
  const [pendingBackwardMove, setPendingBackwardMove] = useState<{
    quoteId: string;
    quoteNumber: string | null;
    fromStage: StageKey;
    targetStage: StageKey;
  } | null>(null);

  const { data, isLoading, isFetching, isError, refetch } = useProjectsKanban(filters);
  const createProject = useCreateProject();
  const updateProject = useUpdateProject();
  const advanceStage = useAdvanceProjectStage();
  const updatePositions = useUpdateProjectPositions();

  // Detail panel data
  const selectedProjectId = selectedQuote?.supportProjectId || null;
  const { data: selectedProject, isLoading: projectLoading } = useProject(selectedProjectId ?? undefined);
  const completeMilestone = useCompleteMilestone();
  const uncompleteMilestone = useUncompleteMilestone();
  const updateQuote = useUpdateQuote();
  const deleteQuote = useDeleteQuote();
  const discardQuoteMutation = useDiscardQuote();
  const { data: invoiceQuoteSummary } = useQuoteByNumber(
    isInvoiceWorkflowOpen ? invoiceQuoteNumber || undefined : undefined,
  );
  const { data: invoiceV4Quote } = useQuoteV4(invoiceQuoteSummary?.id);
  const { data: invoiceV3Quote } = useQuoteV3(
    invoiceV4Quote?.mongoQuoteId || invoiceQuoteSummary?.mongoQuoteId,
  );

  const handleProjectClick = useCallback((project: ProjectKanbanCard) => {
    setSelectedQuote(project);
  }, []);

  const initialSupportProjectQuote = useMemo<InitialProjectQuote | null>(() => {
    return toInitialSupportProjectQuote(quoteForSupportProject);
  }, [quoteForSupportProject]);

  const handleEditProject = useCallback(() => {
    if (!selectedProjectId) return;
    setIsEditModalOpen(true);
  }, [selectedProjectId]);

  const openInvoiceWorkflow = useCallback((project: ProjectKanbanCard) => {
    if (!project.quoteNumber) {
      toast.error('This project has no linked quote');
      return;
    }

    setSelectedQuote(project);
    setInvoiceQuoteNumber(project.quoteNumber);
    setIsInvoiceWorkflowOpen(true);
  }, []);

  const handleCreateInvoice = useCallback(() => {
    if (selectedQuote) openInvoiceWorkflow(selectedQuote);
  }, [openInvoiceWorkflow, selectedQuote]);

  // Once a quote is fully invoiced, move its card out of the Invoicing stage
  // to Customer Onboarding (the next stage). Partial invoicing stays put.
  const handleInvoiceCreated = useCallback((fullyInvoiced: boolean) => {
    if (!fullyInvoiced) return;
    const quoteId = selectedQuote?.quoteId || selectedQuote?.id;
    if (quoteId && selectedQuote?.currentStage === 'invoicing') {
      advanceStage.mutate({
        quoteId,
        targetStage: 'customer_onboarding' as ProjectKanbanCard['currentStage'],
      });
    }
  }, [selectedQuote, advanceStage]);

  const handleInvoiceQuoteSubmit = useCallback(
    async (input: CreateQuoteInput | UpdateQuoteInput) => {
      const mongoQuoteId = invoiceV3Quote?._id || invoiceV4Quote?.mongoQuoteId;
      if (!mongoQuoteId) {
        throw new Error('The linked quote is not available in the legacy invoice system');
      }
      // Prefer the PG UUID so V4 read paths (Kanban, Print, lists) reflect the
      // change immediately; updateQuote() routes UUID → PG, Mongo id → legacy.
      await updateQuote.mutateAsync({
        id: invoiceQuoteSummary?.id || mongoQuoteId,
        data: input as UpdateQuoteInput,
      });
    },
    [invoiceQuoteSummary?.id, invoiceV3Quote?._id, invoiceV4Quote?.mongoQuoteId, updateQuote],
  );

  const handleDiscardConfirm = useCallback(() => {
    const quoteId = discardingCard?.quoteId || discardingCard?.id;
    if (!quoteId) return;
    discardQuoteMutation.mutate(quoteId, {
      onSuccess: () => {
        setIsDiscardDialogOpen(false);
        setDiscardingCard(null);
        setSelectedQuote(null);
        void queryClient.invalidateQueries({ queryKey: kanbanKeys.all });
        void queryClient.invalidateQueries({ queryKey: quoteKeys.lists() });
      },
      onError: () => {
        setIsDiscardDialogOpen(false);
        setDiscardingCard(null);
      },
    });
  }, [discardingCard, discardQuoteMutation, queryClient]);

  const handleDeleteConfirm = useCallback(() => {
    const quoteId = deletingCard?.quoteId || deletingCard?.id;
    if (!quoteId) return;
    deleteQuote.mutate(quoteId, {
      onSuccess: () => {
        setIsDeleteDialogOpen(false);
        setDeletingCard(null);
        setSelectedQuote(null);
        void queryClient.invalidateQueries({ queryKey: kanbanKeys.all });
        void queryClient.invalidateQueries({ queryKey: quoteKeys.lists() });
      },
      onError: () => {
        setIsDeleteDialogOpen(false);
        setDeletingCard(null);
      },
    });
  }, [deletingCard, deleteQuote, queryClient]);

  const handleProjectUpdate = useCallback(
    async (input: UpdateProjectInput) => {
      if (!selectedProjectId) return;
      try {
        await updateProject.mutateAsync({ id: selectedProjectId, input });
        toast.success('Project updated successfully');
        setIsEditModalOpen(false);
      } catch {
        toast.error('Failed to update project');
      }
    },
    [selectedProjectId, updateProject]
  );

  const handleCreateSupportProject = useCallback(
    async (input: CreateProjectInput | UpdateProjectInput) => {
      try {
        const createdProject = await createProject.mutateAsync(input as CreateProjectInput);
        toast.success('Supporting project created');
        setQuoteForSupportProject(null);
        await queryClient.invalidateQueries({ queryKey: kanbanKeys.all });
        setSelectedQuote((current) => {
          const currentQuoteId = current?.quoteId || current?.id;
          if (!current || currentQuoteId !== createdProject.quote_id) return current;
          return {
            ...current,
            supportProjectId: createdProject.id,
            type: createdProject.type,
            assignedToName: createdProject.assigned_to_name || current.assignedToName,
            accountManagerName: createdProject.account_manager_name || current.accountManagerName,
          };
        });
      } catch (error) {
        toast.error(error instanceof Error ? error.message : 'Failed to create supporting project');
      }
    },
    [createProject, queryClient]
  );

  const handleProjectFormOpenChange = useCallback((open: boolean) => {
    if (isEditModalOpen) {
      setIsEditModalOpen(open);
    }
    if (!open) {
      setQuoteForSupportProject(null);
      setIsEditModalOpen(false);
    }
  }, [isEditModalOpen]);

  const handleAdvanceStage = useCallback((quoteId: string, targetStage: string) => {
    const card = data?.columns
      .flatMap((col) => col.projects)
      .find((p) => p.id === quoteId || p.quoteId === quoteId);

    // Backward move: gate on a mandatory reason (recorded in the immutable audit
    // log). Checked first — a backward drop never targets ready_to_ship.
    const fromStage = card?.currentStage;
    if (fromStage && STAGE_INDEX[targetStage] < STAGE_INDEX[fromStage]) {
      setPendingBackwardMove({
        quoteId,
        quoteNumber: card?.quoteNumber ?? null,
        fromStage,
        targetStage: targetStage as StageKey,
      });
      return;
    }

    if (targetStage === 'ready_to_ship') {
      setPendingReadyToShipMove({
        quoteId,
        quoteNumber: card?.quoteNumber ?? null,
        targetStage: targetStage as ProjectKanbanCard['currentStage'],
      });
      return;
    }
    advanceStage.mutate({ quoteId, targetStage: targetStage as ProjectKanbanCard['currentStage'] });
  }, [advanceStage, data]);

  const handleReadyToShipMacConfirm = useCallback(() => {
    if (!pendingReadyToShipMove) return;
    advanceStage.mutate({ quoteId: pendingReadyToShipMove.quoteId, targetStage: pendingReadyToShipMove.targetStage });
    setPendingReadyToShipMove(null);
  }, [pendingReadyToShipMove, advanceStage]);

  const handleReadyToShipMacCancel = useCallback(() => {
    setPendingReadyToShipMove(null);
  }, []);

  const handleBackwardMoveConfirm = useCallback((reason: string) => {
    if (!pendingBackwardMove) return;
    if (!reason.trim()) return;
    advanceStage.mutate({
      quoteId: pendingBackwardMove.quoteId,
      targetStage: pendingBackwardMove.targetStage,
      reason: reason.trim(),
    });
    setPendingBackwardMove(null);
  }, [pendingBackwardMove, advanceStage]);

  const handleBackwardMoveCancel = useCallback(() => {
    setPendingBackwardMove(null);
  }, []);

  const handleUpdatePositions = useCallback((updates: Array<{ id: string; position: number }>) => {
    updatePositions.mutate({ updates });
  }, [updatePositions]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  // A failed filter refetch has no data for the new query key; returning null
  // here would yank the whole view — including the filter inputs — away.
  if (!data) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center justify-between">
        <span className="text-red-700">{t('kanban.loadError', 'The board failed to load.')}</span>
        {isError && (
          <Button variant="outline" size="sm" onClick={() => refetch()}>
            {t('kanban.retry', 'Retry')}
          </Button>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <OperationsKanbanSummaryBar
        weighted={computeWeightedPipeline(data)}
        summary={data.statusSummary ?? computeStageBucketSummary(data)}
        fromDate={filters.fromDate}
        toDate={filters.toDate}
        onRangeChange={(fromDate, toDate) => setFilters((prev) => ({ ...prev, fromDate, toDate }))}
        loading={isFetching}
      />
      <KanbanToolbar filters={filters} onFiltersChange={setFilters} />

      <ProjectsKanbanBoard
        data={data}
        onAdvanceStage={handleAdvanceStage}
        onUpdatePositions={handleUpdatePositions}
        onProjectClick={handleProjectClick}
        onCreateInvoice={openInvoiceWorkflow}
      />

      <Sheet
        open={!!selectedQuote}
        onOpenChange={(open) => { if (!open) setSelectedQuote(null); }}
      >
        <SheetContent className="p-0 flex flex-col sm:max-w-2xl">
          <SheetTitle className="sr-only">Quote operations details</SheetTitle>
          <SheetDescription className="sr-only">
            Quote fulfillment details including SKUs, dates, notes, tasks, contacts, and activity.
          </SheetDescription>
          <QuoteOperationsDetailPanel
            quoteCard={selectedQuote}
            supportProject={selectedProject}
            supportProjectLoading={projectLoading}
            onAddSupportProject={setQuoteForSupportProject}
            onEditSupportProject={handleEditProject}
            onCreateInvoice={
              selectedQuote?.currentStage === 'invoicing'
                ? handleCreateInvoice
                : undefined
            }
            onDiscard={canDiscardQuote ? () => {
              setDiscardingCard(selectedQuote);
              setIsDiscardDialogOpen(true);
            } : undefined}
            onDelete={canDeleteQuote ? () => {
              setDeletingCard(selectedQuote);
              setIsDeleteDialogOpen(true);
            } : undefined}
            onCompleteStep={(stepKey) => {
              if (selectedProjectId) {
                completeMilestone.mutate({ projectId: selectedProjectId, milestoneKey: stepKey });
              }
            }}
            onUncompleteStep={(stepKey) => {
              if (selectedProjectId) {
                uncompleteMilestone.mutate({ projectId: selectedProjectId, milestoneKey: stepKey });
              }
            }}
            isCompletingStep={completeMilestone.isPending}
          />
        </SheetContent>
      </Sheet>

      <ProjectFormModal
        open={isEditModalOpen || !!quoteForSupportProject}
        onOpenChange={handleProjectFormOpenChange}
        project={isEditModalOpen ? selectedProject : undefined}
        initialQuote={!isEditModalOpen ? initialSupportProjectQuote : undefined}
        onSubmit={isEditModalOpen ? handleProjectUpdate : handleCreateSupportProject}
        isSubmitting={isEditModalOpen ? updateProject.isPending : createProject.isPending}
      />

      <QuoteFormModal
        open={isInvoiceWorkflowOpen && !!invoiceV3Quote}
        onOpenChange={(open) => {
          setIsInvoiceWorkflowOpen(open);
          if (!open) setInvoiceQuoteNumber(null);
        }}
        quote={invoiceV3Quote || null}
        v4Quote={invoiceV4Quote || null}
        pgQuoteId={invoiceQuoteSummary?.id}
        onSubmit={handleInvoiceQuoteSubmit}
        isLoading={updateQuote.isPending}
        invoiceWorkflow
        onInvoiceCreated={handleInvoiceCreated}
        initialCustomerData={invoiceV4Quote ? {
          accountName: invoiceV4Quote.accountName || undefined,
          accountNumber: invoiceV4Quote.accountNumber || undefined,
          contractId: invoiceV4Quote.subscriptionId || undefined,
          salesManagerId: invoiceV4Quote.salesManagerId || undefined,
          contractualManagerId: invoiceV4Quote.contractualManagerId || undefined,
          pgAccountId: invoiceV4Quote.accountId || undefined,
          lockCustomer: true,
        } : undefined}
      />

      <AlertDialog open={isDiscardDialogOpen} onOpenChange={setIsDiscardDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('discard.title')}</AlertDialogTitle>
            <AlertDialogDescription>{t('discard.message')}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={discardQuoteMutation.isPending}>{t('discard.cancel')}</AlertDialogCancel>
            <AlertDialogAction onClick={handleDiscardConfirm} disabled={discardQuoteMutation.isPending}>
              {t('discard.confirm')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('delete.title')}</AlertDialogTitle>
            <AlertDialogDescription>{t('delete.message')}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleteQuote.isPending}>{t('delete.cancel')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteConfirm}
              disabled={deleteQuote.isPending}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {t('delete.confirm')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <ShippingMacValidationDialog
        open={!!pendingReadyToShipMove}
        quoteId={pendingReadyToShipMove?.quoteId}
        quoteNumber={pendingReadyToShipMove?.quoteNumber}
        onConfirm={handleReadyToShipMacConfirm}
        onCancel={handleReadyToShipMacCancel}
      />

      <BackwardMoveReasonDialog
        open={!!pendingBackwardMove}
        quoteNumber={pendingBackwardMove?.quoteNumber}
        fromStageLabel={pendingBackwardMove ? (STAGE_LABEL[pendingBackwardMove.fromStage] ?? pendingBackwardMove.fromStage) : ''}
        toStageLabel={pendingBackwardMove ? (STAGE_LABEL[pendingBackwardMove.targetStage] ?? pendingBackwardMove.targetStage) : ''}
        pending={advanceStage.isPending}
        onConfirm={handleBackwardMoveConfirm}
        onCancel={handleBackwardMoveCancel}
      />
    </div>
  );
}
